import { useQuery } from '@tanstack/react-query';
import type { TaskStatus } from 'scheduler-types';
import { apiFetch } from '../lib/api';

export interface TaskDependency {
  taskId: string;
  dependencyType: string;
  status: string;
}

export interface TaskWithDependencies {
  id: string;
  laborBucket: string;
  status: TaskStatus;
  estimatedMinutesMin: number;
  estimatedMinutesMax: number;
  actualMinutes: number | null;
  scheduledStart: string | null;
  scheduledEnd: string | null;
  assignedPerson: { id: string; name: string } | null;
  isPinned: boolean;
  isExpediteEligible: boolean;
  dependencies: TaskDependency[];
}

interface TaskDetailResponse {
  data: {
    id: string;
    laborBucket: string;
    status: TaskStatus;
    estimatedMinutesMin: number;
    estimatedMinutesMax: number;
    actualMinutes: number | null;
    scheduledStart: string | null;
    scheduledEnd: string | null;
    assignedPerson: { id: string; name: string } | null;
    isPinned: boolean;
    isExpediteEligible: boolean;
    dependencies: TaskDependency[];
  };
}

async function fetchTaskDetails(taskId: string): Promise<TaskWithDependencies> {
  const response = await apiFetch<TaskDetailResponse>(`/api/v1/tasks/${taskId}`);
  const d = response.data;
  return {
    id: d.id,
    laborBucket: d.laborBucket,
    status: d.status,
    estimatedMinutesMin: d.estimatedMinutesMin,
    estimatedMinutesMax: d.estimatedMinutesMax,
    actualMinutes: d.actualMinutes,
    scheduledStart: d.scheduledStart,
    scheduledEnd: d.scheduledEnd,
    assignedPerson: d.assignedPerson,
    isPinned: d.isPinned,
    isExpediteEligible: d.isExpediteEligible,
    dependencies: d.dependencies,
  };
}

async function fetchAllTaskDependencies(
  taskIds: string[]
): Promise<TaskWithDependencies[]> {
  // Fetch all task details in parallel (batched in groups of 10 to avoid overwhelming the API)
  const batchSize = 10;
  const results: TaskWithDependencies[] = [];

  for (let i = 0; i < taskIds.length; i += batchSize) {
    const batch = taskIds.slice(i, i + batchSize);
    const batchResults = await Promise.all(batch.map(fetchTaskDetails));
    results.push(...batchResults);
  }

  return results;
}

/**
 * Fetches full task details (including dependencies) for a list of task IDs.
 * Used by the TaskFlowChart to build the dependency graph.
 */
export function useTaskDependencies(taskIds: string[] | undefined) {
  return useQuery({
    queryKey: ['taskDependencies', taskIds],
    queryFn: () => fetchAllTaskDependencies(taskIds!),
    enabled: !!taskIds && taskIds.length > 0,
    staleTime: 60000,
  });
}
